import { toast } from 'react-toastify';
import * as yup from 'yup';

import actions from '../../../constants/actionTypes';
import request from '../../../utils/request';
import { formatDate, isSuccess, sendNotify } from '../../../utils/utils';
import { setGeneralError } from '../../../layout/components/UniversalModal/actions';
import { startAppLoading, stopAppLoading } from '../../../layout/actions';
import { trans } from '../../../localization';
import {
  EnumBookingNowShipStatusBrief,
  EnumPage,
} from '../../../constants/enum';
import { closeDialogGeneric } from '../../../redux/genericAction';

const PAGE = EnumPage.NOW_SHIP_ORDER_MANAGEMENT.id;

async function checkNewMessage(bookingCodes) {
  const response = await request.postNowShip('/api/chat/check_new_message', {
    booking_codes: bookingCodes,
  });
  if (isSuccess(response)) {
    return response.reply;
  }
  return {};
}

export function getListOrderNowShip() {
  function validate(state, dispatchGeneralError) {
    const { filters } = state;
    const schema = yup.object().shape({
      fromDate: yup
        .date()
        .max(yup.ref('toDate'), trans('nex_from_date_to_date_not_correct'))
        .required(),
      toDate: yup.date().required(),
      orderCode: yup.string().max(12, 'nex_order_code_length_too_long'),
      status: yup
        .object()
        .required()
        .shape({
          value: yup.number().required(),
        }),
    });
    try {
      schema.validateSync(filters);
      return true;
    } catch (err) {
      dispatchGeneralError(err.errors[0]);
      return false;
    }
  }

  function genBody(state, restaurantId) {
    const { filters, table } = state;
    const { activePage, pageSize } = table;
    const {
      fromDate, toDate, status, orderCode,
    } = filters;
    const body = {
      offset: (activePage - 1) * pageSize,
      limit: pageSize,
      referral_ids: [restaurantId],
      from_time: formatDate(fromDate),
      to_time: formatDate(toDate),
      group_status: status.value,
    };
    if (orderCode.length > 0) {
      body.booking_code = orderCode;
    }
    return body;
  }

  return (dispatch, getState) => {
    const state = getState().ship.order;
    if (!validate(state, error => dispatch(setGeneralError(error)))) {
      return null;
    }
    const restaurantId = getState().ship.general.currentRestaurant
      .restaurant_id;
    const body = genBody(state, restaurantId);
    dispatch(startAppLoading());
    return request
      .postNowShip('/api/merchant/booking/search', body)
      .then(async (response) => {
        if (isSuccess(response)) {
          const { bookings: itemList, total: totalSize } = response.reply;
          let resultItemList = itemList;
          if (
            state.filters.status.id ===
            EnumBookingNowShipStatusBrief.IN_PROGRESS.id
          ) {
            const checkNewMessageMap = await checkNewMessage(
              itemList.map(item => item.code),
            );
            resultItemList = resultItemList.map(item => ({
              ...item,
              hasNewMessage: !!checkNewMessageMap[item.code],
            }));
          }
          dispatch({
            type: actions.RECEIVE_ORDER_MANAGEMENT_LIST_NOW_SHIP,
            response: {
              itemList: resultItemList,
              totalSize,
            },
          });
        } else {
          dispatch(setGeneralError(trans(response.result)));
        }
        dispatch(stopAppLoading());
      });
  };
}

export function getDetailOrderNowShip(
  bookingCode,
  isSilent = false,
  referralId,
) {
  return (dispatch, getState) => {
    const restaurantId =
      referralId || getState().ship.general.currentRestaurant.restaurant_id;

    if (!isSilent) {
      dispatch(startAppLoading());
    }
    const getDetailPromise = request
      .getNowShip('/api/booking/get_detail', {
        booking_code: bookingCode,
        referral_id: restaurantId,
      })
      .then((response) => {
        if (isSuccess(response)) {
          if (!isSilent) {
            dispatch(stopAppLoading());
          }
          return { ...response.reply, restaurantId };
        }
        dispatch(setGeneralError(trans(response.result)));
        if (!isSilent) {
          dispatch(stopAppLoading());
        }
        return {};
      });
    const checkNewMessagePromise = checkNewMessage([bookingCode]).then(
      response => ({ hasNewMessage: !!response[bookingCode] }),
    );
    return Promise.all([getDetailPromise, checkNewMessagePromise]).then(
      responses =>
        responses.reduce(
          (response, item) => ({
            ...response,
            ...item,
          }),
          {},
        ),
    );
  };
}

export function submitFeedbackNowShip(
  bookingCode,
  restaurantId,
  comment,
  rating,
) {
  return (dispatch) => {
    dispatch(startAppLoading());
    return request
      .postNowShip('/api/booking/feedback', {
        booking_code: bookingCode,
        referral_id: restaurantId,
        comment,
        rating_level_id: rating.level,
        rating_detail_ids: rating.details,
      })
      .then((response) => {
        if (isSuccess(response)) {
          dispatch(stopAppLoading());
          return true;
        }
        dispatch(stopAppLoading());
        dispatch(setGeneralError(response.result));
        return false;
      });
  };
}

export function deleteOrderNowShip(bookingCode, restaurantId) {
  return (dispatch) => {
    dispatch(startAppLoading());
    return request
      .postNowShip('/api/booking/user_delete', {
        booking_code: bookingCode,
        referral_id: restaurantId,
      })
      .then((response) => {
        if (isSuccess(response)) {
          return dispatch(getListOrderNowShip()).then(() =>
            dispatch(closeDialogGeneric(PAGE)),
          );
        }
        dispatch(stopAppLoading());
        return dispatch(setGeneralError(response.result));
      });
  };
}

export function reportOrderNowShip(bookingCode, restaurantId, content) {
  return (dispatch) => {
    dispatch(startAppLoading());
    return request
      .postNowShip('/api/booking/user_report', {
        booking_code: bookingCode,
        referral_id: restaurantId,
        content,
      })
      .then((response) => {
        if (isSuccess(response)) {
          dispatch(stopAppLoading());
          return true;
        }
        dispatch(stopAppLoading());
        dispatch(setGeneralError(response.result));
        return false;
      });
  };
}

export function sendNotifyOrderNowShip(pushData) {
  const { message, booking_code: bookingCode, status } = pushData;
  const { body, title } = message.data;
  if (body && title) {
    if (document.hidden) {
      sendNotify({ message: title, detail: body });
    } else {
      const toastId = `${bookingCode}-${status}`;
      if (!toast.isActive(toastId)) {
        toast.info(body, {
          toastId,
        });
      }
    }
  }
}

export function changeStatusOrderNowShip(pushData) {
  return (dispatch) => {
    const { booking_code: bookingCode, status } = pushData;
    dispatch({
      type: actions.CHANGE_ORDER_STATUS_NOW_SHIP,
      response: {
        bookingCode,
        status,
      },
    });
  };
}

export function cancelOrderNowShip(
  bookingCode,
  {
    restaurantId, fromStatus, comment, templateNoteContent, templateNoteId,
  },
) {
  return dispatch =>
    request
      .postNowShip('/api/booking/cancel', {
        booking_code: bookingCode,
        referral_id: restaurantId,
        from_status_id: fromStatus,
        comment,
        template_note_content: templateNoteContent,
        template_note_id: templateNoteId,
      })
      .then((response) => {
        if (isSuccess(response)) {
          dispatch(stopAppLoading());
          dispatch(getListOrderNowShip());
          toast.success(trans('nex_cancel_success'));
          return true;
        }
        dispatch(stopAppLoading());
        dispatch(setGeneralError(response.result));
        return false;
      });
}
export function retryOrderNowShip(bookingCode, restaurantId, fromStatus) {
  return dispatch =>
    request
      .postNowShip('/api/booking/retry', {
        booking_code: bookingCode,
        referral_id: restaurantId,
        from_status_id: fromStatus,
      })
      .then((response) => {
        if (isSuccess(response)) {
          dispatch(stopAppLoading());
          return true;
        }
        dispatch(stopAppLoading());
        dispatch(setGeneralError(response.result));
        return false;
      });
}

export function openDialogCanceledReason(itemDetail) {
  return {
    type: actions.OPEN_DIALOG_CANCELED_REASON,
    response: {
      itemDetail,
    },
  };
}

export function closeDialogCanceledReason() {
  return {
    type: actions.CLOSE_DIALOG_CANCELED_REASON,
  };
}

export function getReturnCode(bookingCode) {
  return (dispatch, getState) => {
    const {
      restaurant_id: restaurantId,
    } = getState().ship.general.currentRestaurant;
    return request
      .postNowShip('/api/booking/get_return_parcel_code', {
        booking_code: `${bookingCode}`,
        referral_id: restaurantId,
      })
      .then((response) => {
        if (isSuccess(response)) {
          return response.reply.code;
        }
        return false;
      })
      .catch(() => false);
  };
}



// WEBPACK FOOTER //
// ./foody/src/pages/NowShip/OrderManagementNowShip/actions.js